import React from "react";
import {
  Box,
  CircularProgress,
  Typography,
  Fade,
  useTheme,
  alpha,
} from "@mui/material";

interface LoadingSpinnerProps {
  /** Custom loading message to display */
  message?: string;
  /** Size of the spinner (small, medium, large, or number) */
  size?: "small" | "medium" | "large" | number;
  /** Whether to show backdrop overlay */
  backdrop?: boolean;
  /** Whether to center on full screen or inline */
  fullScreen?: boolean;
  /** Custom color for the spinner */
  color?:
    | "primary"
    | "secondary"
    | "error"
    | "info"
    | "success"
    | "warning"
    | "inherit";
  /** Delay before showing spinner (prevents flash for quick loads) */
  delay?: number;
  /** Show/hide the component */
  show?: boolean;
}

const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({
  message = "Loading...",
  size = "large",
  backdrop = true,
  fullScreen = false,
  color = "primary",
  delay = 200,
  show = true,
}) => {
  const theme = useTheme();
  const [shouldShow, setShouldShow] = React.useState(!delay);

  // Handle delay before showing spinner
  React.useEffect(() => {
    if (!show) {
      setShouldShow(false);
      return;
    }

    if (delay > 0) {
      const timer = setTimeout(() => setShouldShow(true), delay);
      return () => clearTimeout(timer);
    } else {
      setShouldShow(true);
    }
  }, [show, delay]);

  // Don't render if not showing
  if (!show || !shouldShow) {
    return null;
  }

  // Convert size prop to pixel value
  const getSpinnerSize = () => {
    if (typeof size === "number") return size;
    switch (size) {
      case "small":
        return 32;
      case "medium":
        return 48;
      case "large":
        return 64;
      default:
        return 64;
    }
  };

  const spinnerSize = getSpinnerSize();

  const content = (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      gap={2}
      role="status"
      aria-live="polite"
      aria-label={message}
    >
      <CircularProgress
        size={spinnerSize}
        thickness={4}
        color={color}
        aria-hidden="true"
      />
      {message && (
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{
            fontSize: { xs: "0.875rem", sm: "1rem" },
            fontWeight: 500,
            textAlign: "center",
            maxWidth: "300px",
            px: 2,
          }}
        >
          {message}
        </Typography>
      )}
    </Box>
  );

  if (!fullScreen) {
    // Inline spinner
    return (
      <Fade in={shouldShow} timeout={300}>
        <Box display="flex" justifyContent="center" alignItems="center" py={4}>
          {content}
        </Box>
      </Fade>
    );
  }

  // Full screen spinner
  return (
    <Fade in={shouldShow} timeout={300}>
      <Box
        position="fixed"
        top={0}
        left={0}
        right={0}
        bottom={0}
        display="flex"
        alignItems="center"
        justifyContent="center"
        zIndex={9999}
        sx={{
          backgroundColor: backdrop
            ? alpha(theme.palette.background.default, 0.8)
            : "transparent",
          backdropFilter: backdrop ? "blur(4px)" : "none",
        }}
      >
        <Box
          sx={{
            backgroundColor: backdrop
              ? theme.palette.background.paper
              : "transparent",
            borderRadius: backdrop ? 2 : 0,
            p: backdrop ? 4 : 0,
            boxShadow: backdrop ? theme.shadows[8] : "none",
            minWidth: backdrop ? "200px" : "auto",
          }}
        >
          {content}
        </Box>
      </Box>
    </Fade>
  );
};

export default LoadingSpinner;
